import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { rtdb } from "../firebase";
import { ref, get, query, orderByChild, startAt } from "firebase/database";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import FgListDialog from "./FgListDialog";
import { saveAs } from "file-saver";
import "./AnalyticProductionLine.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function AnalyticProductionLine({ onBack, onNavigateToSummary }) {
  const stages = useMemo(() => ["Receive", "Treat", "Paint", "Pack", "Deliver"], []);
  const stageColors = useMemo(() => ["#FF5722", "#4CAF50", "#2196F3", "#FFC107", "#9C27B0"], []);

  const [stageData, setStageData] = useState({});
  const [stageFgNumbers, setStageFgNumbers] = useState({});
  const [selectedFilter, setSelectedFilter] = useState("Day");
  const [loading, setLoading] = useState(true);
  
  // 🌟 Expanded context structure to explicitly capture raw layout dates during point interaction
  const [fgDialog, setFgDialog] = useState({ open: false, stage: "", rawDate: "", fgList: [] });
  const [fgDetailsMap, setFgDetailsMap] = useState({});

  const [filterMode, setFilterMode] = useState("quick");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const parseDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString) || dateString.includes("T")) {
      const d = new Date(dateString);
      return isNaN(d.getTime()) ? null : d;
    }
    // ⚡ Fixed: Removed useless escapes inside the character group
    const parts = dateString.split(/[/-]/); 
    if (parts.length === 3) {
      const [day, month, year] = parts.map((x) => parseInt(x, 10));
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return new Date(year, month - 1, day);
      }
    }
    return null;
  };

  const shouldInclude = useCallback((recordDate, filter) => {
    const now = new Date();
    if (filterMode === "custom") {
      if (!startDate || !endDate) return true;
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return recordDate >= start && recordDate <= end;
    }
    switch (filter) {
      case "Day": return recordDate.toDateString() === now.toDateString();
      case "Week": {
        const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - (dayOfWeek - 1));
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        return recordDate >= startOfWeek && recordDate <= endOfWeek;
      }
      case "Month": return recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
      case "Year": return recordDate.getFullYear() === now.getFullYear();
      default: return true;
    }
  }, [filterMode, startDate, endDate]);

  const formatDateForFilter = (date) => {
    const pad = (n) => (n < 10 ? "0" + n : n);
    if (!(date instanceof Date) || isNaN(date)) return "Invalid Date";
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  // ⚡ Wrapped fetchStageData in useCallback to fix hook dependency chain rule
  const fetchStageData = useCallback(async () => {
    if (filterMode === "custom" && (!startDate || !endDate)) return;

    setLoading(true);
    const newStageData = {};
    const newStageFgNumbers = {};
    const newFgDetailsMap = {};

    try {
      const now = new Date();
      let filterThreshold = "";

      if (filterMode === "custom" && startDate) {
        filterThreshold = startDate;
      } else if (selectedFilter === "Year") {
        filterThreshold = `${now.getFullYear()}-01-01`;
      } else if (selectedFilter === "Month") {
        filterThreshold = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      }

      const stagePromises = stages.map((stage) => {
        const stageRef = ref(rtdb, stage);
        if (filterMode === "custom" || ["Week", "Month", "Year"].includes(selectedFilter)) {
          return get(stageRef);
        }
        if (filterThreshold && selectedFilter !== "All") {
          const dateField = stage === "Receive" ? "receivingDate" : `date${stage}`;
          return get(query(stageRef, orderByChild(dateField), startAt(filterThreshold)));
        }
        return get(stageRef);
      });

      const snapshots = await Promise.all(stagePromises);

      snapshots.forEach((snapshot, index) => {
        const stage = stages[index];
        const stageMap = {};

        snapshot.forEach((child) => {
          const data = child.val();
          if (!data) return;

          const fgNumber = data.fgNumber || child.key || "";

          let dateString = "";
          switch (stage) {
            case "Receive": dateString = data.receivingDate; break;
            case "Treat":   dateString = data.dateTreat; break;
            case "Paint":   dateString = data.datePaint; break;
            case "Pack":    dateString = data.datePack; break;
            case "Deliver": dateString = data.dateDeliver; break;
            default:        dateString = "";
          }

          if (!dateString) return;

          const parsedDate = parseDate(dateString);
          if (!parsedDate || isNaN(parsedDate.getTime())) return;

          if (!shouldInclude(parsedDate, selectedFilter)) return;

          const formattedDate = formatDateForFilter(parsedDate);
          stageMap[formattedDate] = (stageMap[formattedDate] || 0) + 1;

          const key = `${stage}|${formattedDate}`;
          if (!newStageFgNumbers[key]) newStageFgNumbers[key] = [];
          if (!newStageFgNumbers[key].includes(fgNumber)) {
            newStageFgNumbers[key].push(fgNumber);
          }

          if (!newFgDetailsMap[fgNumber]) newFgDetailsMap[fgNumber] = {};
          newFgDetailsMap[fgNumber][stage] = data;
        });

        newStageData[stage] = stageMap;
      });

      setStageData(newStageData);
      setStageFgNumbers(newStageFgNumbers);
      setFgDetailsMap(newFgDetailsMap);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedFilter, filterMode, startDate, endDate, stages, shouldInclude]);

  useEffect(() => {
    fetchStageData();
  }, [fetchStageData]); // ⚡ Safely included as verified hook payload dependency

  const chartData = useMemo(() => {
    const allDates = Array.from(
      new Set(Object.values(stageData).flatMap((m) => Object.keys(m)))
    )
      .filter((d) => d && d !== "Invalid Date")
      .sort((a, b) => {
        const [da, ma, ya] = a.split("/").map(Number);
        const [db, mb, yb] = b.split("/").map(Number);
        return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
      });

    const datasets = stages.map((stage, index) => ({
      label: stage,
      data: allDates.map((date) => stageData[stage]?.[date] || 0),
      backgroundColor: stageColors[index] + "BB",
      borderColor: stageColors[index],
      borderWidth: 2,
      borderRadius: 6,
      borderSkipped: false,
      hoverBackgroundColor: stageColors[index],
      hoverBorderWidth: 3,
      hoverBorderColor: "#fff",
    }));

    return { labels: allDates, datasets };
  }, [stageData, stages, stageColors]);

  // 🔹 Memoized options payload containing internal context resolvers
  const chartOptions = useMemo(() => {
    const isTodayQuickFilter = selectedFilter === "Day" && filterMode === "quick";

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 2000,
        easing: "easeOutQuart",
      },
      interaction: isTodayQuickFilter
        ? { mode: "nearest", intersect: true }
        : { mode: "index", intersect: false },
      onClick: (event, elements) => {
        if (!elements.length) return;
        if (!isTodayQuickFilter) return;

        const clickedElement = elements[0];
        const stage = chartData.datasets[clickedElement.datasetIndex].label;
        const date = chartData.labels[clickedElement.index];
        
        const key = `${stage}|${date}`;
        const fgList = stageFgNumbers[key] || [];
        
        if (fgList.length === 0) return;
        
        // 🌟 Saved context explicitly to rawDate tracking variable to fix reference leaks
        setFgDialog({ 
          open: true, 
          stage: stage, 
          rawDate: date, 
          fgList 
        });
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            padding: 25,
            font: { size: 12, weight: "600", family: "'Inter', sans-serif" },
            color: "#1a237e",
          },
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#1a237e",
          titleFont: { size: 14, weight: "bold" },
          bodyColor: "#444",
          bodyFont: { size: 13 },
          borderColor: "#e0e0e0",
          borderWidth: 1,
          padding: 12,
          boxPadding: 8,
          cornerRadius: 10,
          displayColors: true,
          callbacks: {
            label: (context) => ` ${context.dataset.label}: ${context.raw} units`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: { size: 10 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 10 } },
        },
      },
    };
  }, [selectedFilter, filterMode, chartData, stageFgNumbers]);

  const exportCsv = () => {
    if (!fgDetailsMap || Object.keys(fgDetailsMap).length === 0) {
      alert("No data to export");
      return;
    }

    const stageOrder = ["Receive", "Treat", "Paint", "Pack", "Deliver"];
    const rows = [
      [
        "FG Number", "Stage", "Include FG", "Manufacturing Date", "Date", "Time",
        "User Name", "Quantity", "DO Number", "Note", "Label Type", "Batch", "Image URL",
        "QR URL", "Verified Timestamp", "Release Note", "NG Material",
        "NG Manufacturing", "Dented", "Rusted", "Other",
      ],
    ];

    const fgRows = [];
    for (const [fgNumber, stageMap] of Object.entries(fgDetailsMap)) {
      const rowsForFG = [];
      let receiveDate = null;
      for (const stage of stageOrder) {
        const fgData = stageMap[stage];
        if (!fgData) continue;
        const rawDate = fgData.dateDeliver || fgData.datePack || fgData.datePaint || fgData.dateTreat || fgData.receivingDate || "";
        const time = fgData.timeDeliver || fgData.timePack || fgData.timePaint || fgData.timeTreat || fgData.receivingTime || "";
        if (!rawDate) continue;
        const parsedDate = parseDate(rawDate);
        if (!parsedDate) continue;

        if (!shouldInclude(parsedDate, selectedFilter)) continue;

        const formattedDate = formatDateForFilter(parsedDate);
        if (stage === "Receive") receiveDate = parsedDate;

        const verifiedTimestampRaw = fgData.verifiedTimestamp || "";
        let verifiedTimestamp = "";
        if (typeof verifiedTimestampRaw === "number" || /^\d{13}$/.test(verifiedTimestampRaw)) {
          const d = new Date(Number(verifiedTimestampRaw));
          verifiedTimestamp = isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB");
        } else {
          verifiedTimestamp = verifiedTimestampRaw;
        }

        let batchVal = "";
        if (stage === "Paint" && fgData.batch) {
          const batchKeys = Object.keys(fgData.batch);
          if (batchKeys.length > 0) {
            const firstBatchKey = batchKeys[0];
            batchVal = fgData.batch[firstBatchKey]?.batchLabel || "";
          }
        }

        rowsForFG.push([
          fgNumber, stage, fgData.includeWithFG || "", stage === "Receive" ? fgData.manufacturingDate || "" : "",
          formattedDate, time, fgData.userName || "", fgData.quantity || "", fgData.doNumber || "",
          fgData.note || "", fgData.labelType || "", 
          batchVal, 
          fgData.imageUrl || fgData.checklistImageUrl || "",
          fgData.qrImageUrl || "", verifiedTimestamp, stage === "Pack" ? fgData.releaseNoteNo || "" : "",
          stage === "Pack" ? fgData.NGMaterial || "" : "", stage === "Pack" ? fgData.NGManufacturing || "" : "",
          stage === "Pack" ? fgData.Dented || "" : "", stage === "Pack" ? fgData.Rusted || "" : "",
          stage === "Pack" ? fgData.Other || "" : "",
        ]);
      }
      if (rowsForFG.length > 0) {
        fgRows.push({ date: receiveDate ? receiveDate.getTime() : 0, rows: rowsForFG });
      }
    }

    fgRows.sort((a, b) => b.date - a.date);
    for (const group of fgRows) {
      for (const row of group.rows) {
        const escaped = row.map((v) => `"${String(v).replace(/"/g, '""')}"`);
        rows.push(escaped);
      }
    }

    const csvContent = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `production_export_${new Date().getTime()}.csv`);
  };

  return (
    <div className="analytic-production-root">
      <Box sx={{ mt: { xs: 2, md: 1 }, mb: { xs: 2, md: 4 }, textAlign: "center", px: 2 }}>
        <Typography
          variant="h4"
          fontWeight="700"
          sx={{ color: "#0d47a1", fontSize: "40px", lineHeight: 1.2 }}
        >
          Analytic Production Line
        </Typography>
      </Box>

      <div className="filter-export-row">
        <div className="filter-zone left-area mobile-stack">
          <div className="filter-stack">
            <div className="select-wrapper">
              <label>Mode:</label>
              <select value={filterMode} onChange={(e) => setFilterMode(e.target.value)}>
                <option value="quick">Calendar</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>

            <div className="filter-context-area">
              {filterMode === "quick" ? (
                <div className="select-wrapper">
                  <label>Filter:</label>
                  <select value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)}>
                    <option value="All">All Data</option>
                    <option value="Day">Today</option>
                    <option value="Week">This Week</option>
                    <option value="Month">This Month</option>
                    <option value="Year">This Year</option>
                  </select>
                </div>
              ) : (
                <div className="date-picker-group">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  <span className="to-text">to</span>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="filter-zone right-area">
          <button className="export-btn" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>

      <div className="chart-container single-chart-enhanced">
      {loading ? (
        <div className="spinner-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '16px' }}>
          <CircularProgress size={50} thickness={4.5} sx={{ color: "#0d47a1" }} />
          <Typography variant="body1" fontWeight="600" sx={{ color: "#555" }}>
            Analyzing Production Flows...
          </Typography>
        </div>
      ) : (
          <>
            <div className="chart-card-enhanced">
              <div className="chart-header mobile-column">
                <div>
                  <h3>Production Progress</h3>
                    <p className="chart-subtitle">
                      {selectedFilter === "Day" && filterMode === "quick" 
                        ? "Tap bars to inspect FG details" 
                        : "Timeline View Summary"}
                    </p>
                </div>
                <div className="chart-stats-mini">
                  <span className="stat-badge">Live</span>
                </div>
              </div>

              <div className="chart-wrapper">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>

            <div className="back-btn-container">
              <button className="back-btn-wow" onClick={onBack}>
                <span className="btn-icon">←</span>
                <span className="btn-text">Back</span>
              </button>
            </div>
          </>
        )}
      </div>

      {fgDialog.open && (
        <FgListDialog
          fgList={fgDialog.fgList}
          stage={`${fgDialog.stage} — ${fgDialog.rawDate}`}
          onClose={() => setFgDialog({ open: false, stage: "", rawDate: "", fgList: [] })}
          onItemClick={(fgNumber) => {
            const preservedStage = fgDialog.stage;
            const preservedDate = fgDialog.rawDate;

            // 1. Clean up local modal viewport states
            setFgDialog({ open: false, stage: "", rawDate: "", fgList: [] });
            
            // 2. Fire structural layout sync with correctly evaluated parameters
            if (typeof onNavigateToSummary === "function") {
              onNavigateToSummary(`${fgNumber} — ${preservedStage} — ${preservedDate}`);
            }
          }}
        />
      )}
    </div>
  );
}